import { useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  type QuestionInput,
  type QuestionOption,
  type RiskTypeCatalogueEntry,
  type SaveVersionResult,
  type Section,
  type SectionInput,
  type VisibleWhen,
} from '../lib/api'
import { ErrorBox } from './DataTable'
import { Picker, Status, Toggletip } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'
import { QuestionTable } from './QuestionTable'

// The eight the schema permits. The four marked scored are the ones whose
// answer is picked from a list, which is the only case where an option score
// exists for a risk model to multiply a weight by.
const ANSWER_TYPES: { value: string; label: string; scored: boolean }[] = [
  { value: 'SINGLE_CHOICE', label: 'Single choice', scored: true },
  { value: 'MULTI_CHOICE', label: 'Multiple choice', scored: true },
  { value: 'BOOLEAN', label: 'Yes or no', scored: true },
  { value: 'SCALE', label: 'Scale', scored: true },
  { value: 'NUMBER', label: 'Number', scored: false },
  { value: 'DATE', label: 'Date', scored: false },
  { value: 'TEXT', label: 'Free text', scored: false },
  { value: 'FILE', label: 'File only', scored: false },
]

const SCORED = new Set(ANSWER_TYPES.filter((t) => t.scored).map((t) => t.value))

// Mirrors the server's keyShape. Checking here does not replace the server
// check; it stops an author discovering on save that eleven keys are wrong.
const KEY_SHAPE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,62}$/

const BOOLEAN_OPTIONS: QuestionOption[] = [
  { value: 'YES', label: 'Yes', score: 1 },
  { value: 'NO', label: 'No', score: 0 },
]

function toInput(sections: Section[]): SectionInput[] {
  return sections.map((s) => ({
    key: s.sectionKey,
    title: s.title,
    description: s.description,
    riskType: s.riskType || undefined,
    questions: s.questions.map((q) => ({
      key: q.questionKey,
      prompt: q.prompt,
      helpText: q.helpText,
      answerType: q.answerType,
      options: q.options ?? [],
      isRequired: q.isRequired,
      allowsNa: q.allowsNa,
      evidenceRequired: q.evidenceRequired,
      visibleWhen: asCondition(q.visibleWhen),
      citations: q.citations ?? [],
    })),
  }))
}

// A condition read back off the wire is unknown until it is checked. Anything
// that is not the one accepted form is dropped rather than carried, because
// carrying a shape the server refuses turns every later save into a validation
// failure the author cannot see the cause of.
function asCondition(raw: unknown): VisibleWhen | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const key = typeof o.question_key === 'string' ? o.question_key : ''
  const values = Array.isArray(o.in) ? o.in.filter((v): v is string => typeof v === 'string') : []
  if (!key) return null
  return { question_key: key, in: values }
}

function blankQuestion(n: number): QuestionInput {
  return {
    key: 'q-' + n,
    prompt: '',
    helpText: '',
    answerType: 'SINGLE_CHOICE',
    options: [
      { value: 'YES', label: 'Yes', score: 1 },
      { value: 'NO', label: 'No', score: 0 },
    ],
    isRequired: true,
    allowsNa: false,
    evidenceRequired: false,
    visibleWhen: null,
    citations: [],
  }
}

// Option values are compared exactly by the edge, so case is preserved here
// rather than folded. YES and Yes are two different answers to it.
function splitAnswers(raw: string): string[] {
  return raw
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

// Returns the list unchanged when either index is out of range, so a move at
// the edge of a list is a no-op rather than a hole in it.
function swap<T>(list: T[], i: number, j: number): T[] {
  const a = list[i]
  const b = list[j]
  if (a === undefined || b === undefined) return list
  const next = [...list]
  next[i] = b
  next[j] = a
  return next
}

// Changing an answer type away from a list type discards options that could
// never be read again, and towards one seeds a pair rather than leaving an
// author staring at a scored question with nothing to score.
function optionsFor(answerType: string, current: QuestionOption[]): QuestionOption[] {
  if (!SCORED.has(answerType)) return []
  if (answerType === 'BOOLEAN') return BOOLEAN_OPTIONS.map((o) => ({ ...o }))
  return current.length > 0 ? current : BOOLEAN_OPTIONS.map((o) => ({ ...o }))
}

function setOption(q: QuestionInput, oi: number, patch: Partial<QuestionOption>): QuestionInput {
  return { ...q, options: q.options.map((o, i) => (i === oi ? { ...o, ...patch } : o)) }
}

// tip is the reason behind a problem where there is one. The list says what is
// wrong in a line an author can scan; why it matters is one press away.
type Problem = { where: string; detail: string; tip?: string }

// Everything the server would refuse, found before the round trip. The server
// runs its own copy of all of this; this exists so that an author fixing eleven
// keys does it in one pass rather than eleven.
function problemsIn(sections: SectionInput[]): Problem[] {
  const out: Problem[] = []
  const sectionKeys = new Set<string>()
  const questionKeys = new Map<string, string>()
  // Collected up front because a condition may point forward to a question
  // declared later in the version, which is legal.
  const allKeys = new Set<string>()
  for (const sec of sections) {
    for (const q of sec.questions) allKeys.add(q.key.trim().toLowerCase())
  }

  if (sections.length === 0) out.push({ where: 'version', detail: 'A version with no sections cannot be published' })

  sections.forEach((s, si) => {
    const at = 'Section ' + (si + 1)
    if (!s.key.trim()) out.push({ where: at, detail: 'The section needs a key' })
    else if (!KEY_SHAPE.test(s.key)) out.push({ where: at, detail: 'Key ' + s.key + ' is not a permitted key shape' })
    else if (sectionKeys.has(s.key.toLowerCase()))
      out.push({ where: at, detail: 'Key ' + s.key + ' repeats a section key already used' })
    sectionKeys.add(s.key.toLowerCase())

    if (!s.title.trim()) out.push({ where: at, detail: 'The section needs a title' })
    if (s.questions.length === 0) out.push({ where: at, detail: 'A section with no questions is not answerable' })

    s.questions.forEach((q, qi) => {
      const qat = at + ', question ' + (qi + 1)
      if (!q.key.trim()) out.push({ where: qat, detail: 'The question needs a key' })
      else if (!KEY_SHAPE.test(q.key))
        out.push({ where: qat, detail: 'Key ' + q.key + ' is not a permitted key shape' })
      else {
        const lower = q.key.toLowerCase()
        const seen = questionKeys.get(lower)
        if (seen && seen !== q.key) {
          out.push({
            where: qat,
            detail: 'Key ' + q.key + ' differs from ' + seen + ' only in case',
            tip: 'A weight is looked up by exact key, so these read as one question and score as two.',
          })
        } else if (seen) {
          out.push({ where: qat, detail: 'Key ' + q.key + ' is used twice in this version' })
        }
        questionKeys.set(lower, q.key)
      }

      if (!q.prompt.trim()) out.push({ where: qat, detail: 'The question needs a prompt' })

      const scored = SCORED.has(q.answerType)
      if (scored && q.answerType !== 'BOOLEAN' && q.options.length < 2) {
        out.push({
          where: qat,
          detail: 'A ' + q.answerType + ' question needs at least two options',
          tip: 'The option score is what the risk model multiplies its weight by.',
        })
      }
      if (!scored && q.options.length > 0) {
        out.push({
          where: qat,
          detail: 'A ' + q.answerType + ' answer is not picked from a list, so its options would never be read',
        })
      }
      // The server refuses a condition four ways, and all four hide the
      // question from every vendor for ever while the edge treats it as merely
      // unanswered, so nothing downstream would ever report it.
      const cond = q.visibleWhen
      if (cond) {
        const ref = cond.question_key.trim().toLowerCase()
        if (!ref) {
          out.push({ where: qat, detail: 'The condition names no question' })
        } else if (ref === q.key.trim().toLowerCase()) {
          out.push({ where: qat, detail: 'A question cannot condition on its own answer' })
        } else if (!allKeys.has(ref)) {
          out.push({
            where: qat,
            detail: 'It conditions on ' + cond.question_key + ', which is not in this version',
            tip: 'A question conditioned on a key that is not in the version would be hidden from every vendor and never reported.',
          })
        }
        if (cond.in.length === 0) {
          out.push({ where: qat, detail: 'The condition matches no answer, so nothing would ever reveal the question' })
        }
      }

      q.options.forEach((o, oi) => {
        if (!o.value.trim()) out.push({ where: qat + ', option ' + (oi + 1), detail: 'An option needs a value' })
        if (o.score !== undefined && (o.score < 0 || o.score > 1))
          out.push({
            where: qat + ', option ' + (oi + 1),
            detail: 'An option score is a multiplier between 0 and 1',
            tip: 'The weight lives in the risk model.',
          })
      })
    })
  })
  return out
}

type Props = {
  versionId: string
  sections: Section[]
  editable: boolean
  editNote?: string
  onSaved: (result: SaveVersionResult) => void
}

export function QuestionnaireEditor({ versionId, sections, editable, editNote, onSaved }: Props) {
  const [draft, setDraft] = useState<SectionInput[]>(() => toInput(sections))
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [result, setResult] = useState<SaveVersionResult | null>(null)
  const [notes, setNotes] = useState('')
  // The catalogue is the API's. Holding a copy here is how the studio and the
  // schema CHECK would come to disagree, so the select is fed from the wire and
  // an unreachable catalogue keeps whatever type a section already carries.
  const [catalogue, setCatalogue] = useState<RiskTypeCatalogueEntry[] | null>(null)
  const [catalogueDown, setCatalogueDown] = useState(false)

  useEffect(() => {
    let live = true
    api
      .riskTypes()
      .then((r) => {
        if (live) setCatalogue(r.riskTypes)
      })
      .catch(() => {
        if (live) setCatalogueDown(true)
      })
    return () => {
      live = false
    }
  }, [])

  const typeName = (code: string | undefined): string => {
    if (!code) return ''
    return catalogue?.find((t) => t.code === code)?.name ?? code.replace(/_/g, ' ').toLowerCase()
  }

  // The catalogue's reason for a type, or undefined when the catalogue did not
  // list it, which is a different line from a type with an empty reason.
  const typeWhy = (code: string): string | undefined => catalogue?.find((t) => t.code === code)?.why

  const problems = useMemo(() => problemsIn(draft), [draft])
  const questionCount = useMemo(() => draft.reduce((n, s) => n + s.questions.length, 0), [draft])
  const allKeys = useMemo(
    () => draft.flatMap((s) => s.questions.map((q) => q.key)).filter(Boolean),
    [draft],
  )

  // Immutable updates throughout. A deep clone plus index mutation reads
  // shorter but hands every callback an element the compiler cannot prove is
  // there, and the swap helper is where an off-by-one would silently drop a
  // section rather than fail.
  function touch() {
    setDirty(true)
    setResult(null)
  }

  function editSections(fn: (list: SectionInput[]) => SectionInput[]) {
    setDraft(fn)
    touch()
  }

  function editSection(si: number, fn: (s: SectionInput) => SectionInput) {
    editSections((list) => list.map((s, i) => (i === si ? fn(s) : s)))
  }

  function editQuestion(si: number, qi: number, fn: (q: QuestionInput) => QuestionInput) {
    editSection(si, (s) => ({ ...s, questions: s.questions.map((q, i) => (i === qi ? fn(q) : q)) }))
  }

  function save() {
    setSaving(true)
    setSaveError(null)
    api
      .saveQuestionnaireVersion(versionId, draft, notes || undefined)
      .then((r) => {
        setResult(r)
        setDirty(false)
        onSaved(r)
      })
      .catch((err) => setSaveError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setSaving(false))
  }

  if (!editable) {
    return (
      <div className="panel">
        <h2>Questions</h2>
        {/* The API says why this version is closed and how it changes; that
            sentence is kept whole behind the (i) beside a short line. */}
        <p className="note sw-row">
          <Status tone="plain">Frozen</Status>
          <span>This version cannot be edited</span>
          <ReasonTip label="Why this version is frozen" text={editNote} />
        </p>
        <QuestionTable sections={sections} typeName={typeName} answerName={answerTypeLabel} />
      </div>
    )
  }

  return (
    <div className="studio">
      <div className="studio-bar" role="region" aria-label="Draft actions">
        <span>
          <strong>{draft.length}</strong> section{draft.length === 1 ? '' : 's'}, <strong>{questionCount}</strong>{' '}
          question{questionCount === 1 ? '' : 's'}
        </span>
        {problems.length > 0 && (
          <Status tone="crit">
            {problems.length} problem{problems.length === 1 ? '' : 's'}
          </Status>
        )}
        {dirty ? <Status tone="warn">Unsaved</Status> : <Status tone="plain">All saved</Status>}
        <span className="studio-spacer" />
        <input
          type="text"
          aria-label="Note for this save"
          placeholder="What changed, and why"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <button
          type="button"
          className="primary"
          onClick={save}
          disabled={saving || problems.length > 0 || !can('questionnaire.author')}
          title={can('questionnaire.author') ? undefined : refusalReason('questionnaire.author')}
          data-action="tprm.questionnaire.a01"
        >
          {saving ? 'Saving' : 'Save draft'}
        </button>
      </div>

      {saveError && <ErrorBox error={saveError} what="The draft" />}

      {/* The count is the confirmation; the API's note on what a save does is
          kept word for word behind the (i). */}
      {result && (
        <div className="ok-note sw-row" role="status">
          <strong>
            Saved, version {result.versionNo} carries {result.questionCount} question
            {result.questionCount === 1 ? '' : 's'} across {result.sections} section
            {result.sections === 1 ? '' : 's'}
          </strong>
          <ReasonTip label="What saving did" text={result.note} />
        </div>
      )}

      {problems.length > 0 && (
        <div className="issue-list" role="alert">
          <h3>These would be refused on save</h3>
          <ul>
            {problems.map((p, i) => (
              <li key={i}>
                <strong>{p.where}</strong> {p.detail}
                {p.tip && (
                  <>
                    {' '}
                    <Toggletip label="Why this is refused">{p.tip}</Toggletip>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {draft.map((section, si) => (
        <section className="panel studio-section" key={si}>
          <div className="studio-section-head">
            <div className="field">
              <label htmlFor={'sec-key-' + si}>Section key</label>
              <input
                id={'sec-key-' + si}
                className="mono"
                value={section.key}
                spellCheck={false}
                onChange={(e) => editSection(si, (x) => ({ ...x, key: e.target.value }))}
              />
            </div>
            <div className="field studio-grow">
              <label htmlFor={'sec-title-' + si}>Title</label>
              <input
                id={'sec-title-' + si}
                value={section.title}
                onChange={(e) => editSection(si, (x) => ({ ...x, title: e.target.value }))}
              />
            </div>
            <div className="field">
              <label htmlFor={'sec-type-' + si}>Risk type</label>
              <select
                id={'sec-type-' + si}
                value={section.riskType ?? ''}
                aria-describedby={'sec-type-note-' + si}
                onChange={(e) => {
                  const next = e.target.value
                  editSection(si, (x) => ({ ...x, riskType: next || undefined }))
                }}
              >
                <option value="">untyped, feeds the vendor band only</option>
                {(catalogue ?? []).map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
                {/* A type the catalogue did not list, or has not arrived yet,
                    stays selectable so saving does not silently drop it. */}
                {section.riskType && !catalogue?.some((t) => t.code === section.riskType) && (
                  <option value={section.riskType}>{typeName(section.riskType)}</option>
                )}
              </select>
            </div>
            <div className="studio-section-actions">
              <button
                type="button"
                onClick={() => editSections((list) => swap(list, si, si - 1))}
                disabled={si === 0}
                aria-label={'Move section ' + section.title + ' up'}
              >
                Up
              </button>
              <button
                type="button"
                onClick={() => editSections((list) => swap(list, si, si + 1))}
                disabled={si === draft.length - 1}
                aria-label={'Move section ' + section.title + ' down'}
              >
                Down
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => editSections((list) => list.filter((_, i) => i !== si))}
                aria-label={'Remove section ' + section.title}
              >
                Remove
              </button>
            </div>
          </div>

          {/* One line under the type. A catalogue type's own reason is the
              API's sentence and opens from the (i); the console's explanation
              of what a type adds is a toggletip. */}
          <p className="field-note" id={'sec-type-note-' + si}>
            {section.riskType ? (
              typeWhy(section.riskType) !== undefined ? (
                <>
                  Also scored on its own under this type{' '}
                  <ReasonTip label="What this risk type covers" text={typeWhy(section.riskType)} />
                </>
              ) : (
                'Not in the catalogue, so it is kept as it was and checked on save'
              )
            ) : (
              <>
                Answers here count towards the vendor band only{' '}
                <Toggletip label="What a risk type adds">
                  Give the section a type and it is also scored on its own under that type, so a finding can be sent to
                  whoever owns that area.
                </Toggletip>
              </>
            )}
            {catalogueDown && (
              <>
                {' '}
                <Status tone="warn">Types not loaded</Status>{' '}
                <Toggletip label="Why only this type is offered">
                  The risk type catalogue could not be loaded, so only the type already on this section is offered.
                </Toggletip>
              </>
            )}
          </p>

          <div className="field">
            <label htmlFor={'sec-desc-' + si}>Description</label>
            <textarea
              id={'sec-desc-' + si}
              rows={2}
              value={section.description}
              onChange={(e) => editSection(si, (x) => ({ ...x, description: e.target.value }))}
            />
          </div>

          {section.questions.map((q, qi) => (
            <QuestionCard
              key={qi}
              q={q}
              id={si + '-' + qi}
              otherKeys={allKeys.filter((k) => k !== q.key)}
              first={qi === 0}
              last={qi === section.questions.length - 1}
              onChange={(fn) => editQuestion(si, qi, fn)}
              onMoveUp={() => editSection(si, (x) => ({ ...x, questions: swap(x.questions, qi, qi - 1) }))}
              onMoveDown={() => editSection(si, (x) => ({ ...x, questions: swap(x.questions, qi, qi + 1) }))}
              onRemove={() => editSection(si, (x) => ({ ...x, questions: x.questions.filter((_, i) => i !== qi) }))}
            />
          ))}

          <button
            type="button"
            onClick={() => editSection(si, (x) => ({ ...x, questions: [...x.questions, blankQuestion(questionCount + 1)] }))}
          >
            Add a question to {section.title || 'this section'}
          </button>
        </section>
      ))}

      <button
        type="button"
        className="primary"
        onClick={() =>
          editSections((list) => [
            ...list,
            {
              key: 'section-' + (list.length + 1),
              title: '',
              description: '',
              questions: [blankQuestion(questionCount + 1)],
            },
          ])
        }
      >
        Add a section
      </button>
    </div>
  )
}

type CardProps = {
  q: QuestionInput
  id: string
  // Every other question in the version, so a condition is picked rather than
  // typed. A typo here hides the question from every vendor silently.
  otherKeys: string[]
  first: boolean
  last: boolean
  onChange: (fn: (q: QuestionInput) => QuestionInput) => void
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
}

function QuestionCard({ q, id, otherKeys, first, last, onChange, onMoveUp, onMoveDown, onRemove }: CardProps) {
  const scored = SCORED.has(q.answerType)
  const cond = q.visibleWhen

  return (
    <div className="studio-question">
      <div className="studio-question-head">
        <div className="field">
          <label htmlFor={'q-key-' + id}>Question key</label>
          <input
            id={'q-key-' + id}
            className="mono"
            value={q.key}
            spellCheck={false}
            onChange={(e) => onChange((x) => ({ ...x, key: e.target.value }))}
            aria-describedby={'q-key-note-' + id}
          />
        </div>
        <div className="field">
          <label htmlFor={'q-type-' + id}>Answer type</label>
          <select
            id={'q-type-' + id}
            value={q.answerType}
            onChange={(e) => {
              const next = e.target.value
              onChange((x) => ({ ...x, answerType: next, options: optionsFor(next, x.options) }))
            }}
          >
            {ANSWER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="studio-section-actions">
          <button type="button" onClick={onMoveUp} disabled={first} aria-label={'Move ' + q.key + ' up'}>
            Up
          </button>
          <button type="button" onClick={onMoveDown} disabled={last} aria-label={'Move ' + q.key + ' down'}>
            Down
          </button>
          <button type="button" className="danger" onClick={onRemove} aria-label={'Remove ' + q.key}>
            Remove
          </button>
        </div>
      </div>

      <div className="field">
        <label htmlFor={'q-prompt-' + id}>Prompt</label>
        <textarea
          id={'q-prompt-' + id}
          rows={2}
          value={q.prompt}
          onChange={(e) => onChange((x) => ({ ...x, prompt: e.target.value }))}
        />
      </div>

      <div className="field">
        <label htmlFor={'q-help-' + id}>Help text</label>
        <input
          id={'q-help-' + id}
          value={q.helpText}
          onChange={(e) => onChange((x) => ({ ...x, helpText: e.target.value }))}
          aria-describedby={'q-help-note-' + id}
        />
      </div>

      <div className="chiprow">
        <label className="chip-toggle">
          <input
            type="checkbox"
            checked={q.isRequired}
            onChange={(e) => onChange((x) => ({ ...x, isRequired: e.target.checked }))}
          />
          Required
        </label>
        <label className="chip-toggle">
          <input
            type="checkbox"
            checked={q.allowsNa}
            onChange={(e) => onChange((x) => ({ ...x, allowsNa: e.target.checked }))}
          />
          Allows not applicable
        </label>
        <label className="chip-toggle">
          <input
            type="checkbox"
            checked={q.evidenceRequired}
            onChange={(e) => onChange((x) => ({ ...x, evidenceRequired: e.target.checked }))}
          />
          Evidence required
        </label>
      </div>

      {scored && (
        <div className="studio-options">
          <div className="studio-options-head">
            <h4>Options</h4>
          </div>
          {q.options.map((o, oi) => (
            <div className="studio-option" key={oi}>
              <div className="field">
                <label htmlFor={'opt-v-' + id + '-' + oi}>Value</label>
                <input
                  id={'opt-v-' + id + '-' + oi}
                  className="mono"
                  value={o.value}
                  spellCheck={false}
                  onChange={(e) => onChange((x) => setOption(x, oi, { value: e.target.value }))}
                />
              </div>
              <div className="field studio-grow">
                <label htmlFor={'opt-l-' + id + '-' + oi}>Label</label>
                <input
                  id={'opt-l-' + id + '-' + oi}
                  value={o.label ?? ''}
                  onChange={(e) => onChange((x) => setOption(x, oi, { label: e.target.value }))}
                />
              </div>
              <div className="field">
                <label htmlFor={'opt-s-' + id + '-' + oi}>Score</label>
                <input
                  id={'opt-s-' + id + '-' + oi}
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={o.score ?? 0}
                  onChange={(e) => onChange((x) => setOption(x, oi, { score: Number(e.target.value) }))}
                />
              </div>
              <button
                type="button"
                className="danger"
                onClick={() => onChange((x) => ({ ...x, options: x.options.filter((_, i) => i !== oi) }))}
                aria-label={'Remove option ' + o.value}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => onChange((x) => ({ ...x, options: [...x.options, { value: '', label: '', score: 0 }] }))}
          >
            Add an option
          </button>
        </div>
      )}

      <fieldset className="fieldset">
        <legend>Only ask this when</legend>
        <div className="field">
          <label htmlFor={'q-cond-key-' + id}>Another question was answered</label>
          {/* Every other question in the questionnaire: sixty-seven of them
              on a questionnaire of sixty-eight, once per card. */}
          <Picker
            bare
            id={'q-cond-key-' + id}
            label="Another question was answered"
            value={cond?.question_key ?? ''}
            options={otherKeys.map((k) => ({ value: k, label: k }))}
            none="Always ask it"
            placeholder="Search by question key"
            noun="question"
            onChange={(key) =>
              onChange((x) => ({
                ...x,
                visibleWhen: key ? { question_key: key, in: cond?.in ?? [] } : null,
              }))
            }
          />
        </div>

        {cond && (
          <div className="field">
            <label htmlFor={'q-cond-in-' + id}>and the answer was one of</label>
            <input
              id={'q-cond-in-' + id}
              className="mono"
              value={cond.in.join(', ')}
              spellCheck={false}
              placeholder="YES, PARTIAL"
              onChange={(e) =>
                onChange((x) => ({
                  ...x,
                  visibleWhen: { question_key: cond.question_key, in: splitAnswers(e.target.value) },
                }))
              }
              aria-describedby={'q-cond-note-' + id}
            />
          </div>
        )}
      </fieldset>

      <div className="field">
        <label htmlFor={'q-cites-' + id}>Citations</label>
        <input
          id={'q-cites-' + id}
          className="mono"
          value={q.citations.join(', ')}
          spellCheck={false}
          placeholder="RBI-ITSO 5.2, DPDPA s8(5)"
          onChange={(e) =>
            onChange((x) => ({
              ...x,
              citations: e.target.value
                .split(',')
                .map((c) => c.trim())
                .filter(Boolean),
            }))
          }
          aria-describedby={'q-cites-note-' + id}
        />
      </div>
    </div>
  )
}

// A label for an answer type code, for people. The codes stay on the wire.
export function answerTypeLabel(code: string | undefined): string {
  return ANSWER_TYPES.find((t) => t.value === code)?.label ?? (code ?? '').toLowerCase().replace(/_/g, ' ')
}
